import { LitElement, css, html } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { resolveRouterPath } from '../router';

// Database
import { liveQuery } from 'dexie';
import {db, notes, tags, metadata, config} from "../modules/db"

// Models
import { NoteItem } from '../modules/note';

import '@shoelace-style/shoelace/dist/components/card/card.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';

import { styles } from '../styles/shared-styles';

@customElement('app-home')
export class AppHome extends LitElement {

  // For more information on using properties and state in lit
  // check out this link https://lit.dev/docs/components/properties/
  @property() notes = [];
  @property() activeNote: NoteItem;
  @property({ type: Boolean }) activeCommandPallete: boolean = false;
  @property({ type: Boolean }) previewState: boolean = true;

  public notesList: Array<any> = [];

  static styles = [
    styles,
    css`
      main {
      height: 100%;
    }
    `
  ];

  constructor() {
    super();
    this.getNotes();

  }

  async getNotes() {
    this.notes = await db.notes.reverse().toArray();
    if(!this.activeNote) {
      await this.getLastNote();
    }

  }

  async getLastNote() {
  let lastNote = await db.notes.orderBy("id").reverse()
    .limit(1)
    .toArray();

    if(lastNote.length > 0) {
      this.activeNote = await new NoteItem().get(lastNote[0].id);
    }
  }

  firstUpdated() {
    // this method is a lifecycle even in lit
    // for more info check out the lit docs https://lit.dev/docs/components/lifecycle/
    // console.log('This is your home page');
    window.addEventListener('keydown', (e: KeyboardEvent) => {
      // if cmd + shift + p is pressed, toggle the command palette
      if (e.key === 'p' && e.metaKey && e.shiftKey) {
        this.activeCommandPallete = !this.activeCommandPallete;
      }

      if(this.activeCommandPallete && e.key === 'Escape') {
        this.activeCommandPallete = false; // Hide the command palette on Escape key
      }

    });
  }


  async newNote() {
    new NoteItem({
      title: 'Untitled Note',
      content: `---\n\
title: Untitled Note\n\
---\n\n# {{title}}`
    });

    await this.getNotes();
    await this.getLastNote();
  }

  async deleteNoteById(id: string) {
    const note = await new NoteItem().get(id);
    if (note) {
      await this.deleteNote(note);
    }
  }

  private updateNote(noteData: NoteItem) {
    this.activeNote = noteData;
    this.activeNote.update(noteData.title, noteData.content);
  }

  private async deleteNote(note: any) {
    db.notes.delete(note.id);
    db.tags.where('note_id').equals(note.id).delete();
    db.metadata.where('note_id').equals(note.id).delete();
    await this.getNotes();
  }

  private async _handleEvent(e: CustomEvent) {
    if(e.detail.action == 'update-note') {
      if(((new Date()).getTime() - (new Date(e.detail.note.last_updated)).getTime()) > 0) {
        await e.detail.note.update(e.detail.updates.metadata.title, e.detail.updates.content);
        await e.detail.note.updateMeta(e.detail.updates.metadata);

        // check if metadata contains "tags", then split by comma and save to db
        if(e.detail.updates.metadata.tags) {
          const tagsArray = e.detail.updates.metadata.tags.split(',').map(tag => tag.trim());
          // delete existing tags by note_id
          await db.tags.where('note_id').equals(e.detail.note.id).delete();
          await db.tags.bulkPut(tagsArray.map(tag => ({ note_id: e.detail.note.id, name: tag })));
        }

        // Check if metadata contains "folder" then create a folder if it doesn't exist
        if(e.detail.updates.metadata.folder) {
          const folder = e.detail.updates.metadata.folder.trim();
          const existingFolder = await db.folders.where('name').equals(folder).first();
          if (!existingFolder) {
            await db.folders.add({ name: folder });
          }
        }

        await this.getNotes();
      }
    }

    if(e.detail.action == 'add-text') {
      if(this.activeNote) {
        this.activeNote.content += e.detail.text;
        await this.activeNote.update(this.activeNote.title, this.activeNote.content);
        this.activeNote = await new NoteItem().get(this.activeNote.id);
        this.activeCommandPallete = false;
      }
    }

    if(e.detail.action == 'active-note') {
      this.activeNote = await new NoteItem().get(e.detail.id);
    }

    if(e.detail.action == 'delete-note') {
      await this.deleteNoteById(this.activeNote.id);
      this.activeNote = null;
      await this.getNotes();
      this.activeCommandPallete = false;
    }

    if(e.detail.action == 'new-note') {
      this.newNote();
      await this.getNotes();
      this.activeCommandPallete = false;
    }

    if(e.detail.action == 'toggle-command-palette') {
      this.activeCommandPallete = !this.activeCommandPallete;
    }

    // Handle toggle sidebar
    if(e.detail.action == 'toggle-preview') {
      this.previewState = !this.previewState;
      this.requestUpdate();
      this.activeCommandPallete = false;
    }

  }

  render() {
    return html`
      <app-command-palette @events=${this._handleEvent} .active=${this.activeCommandPallete}></app-command-palette>
      <main>
        <app-sidebar @events=${this._handleEvent}></app-sidebar>
        <app-notes-list .activeNote=${this.activeNote} @events=${this._handleEvent} .notes=${this.notes}></app-notes-list>
        <app-note-editor .previewState=${this.previewState} .activeNote=${this.activeNote} @events=${this._handleEvent}></app-note-editor>
      </main>
    `;
  }
}
